import { Breadcrumbs, Tooltip, Typography } from '@mui/material';

import { Link } from 'react-router-dom';

/**
 * Custom Breadcrumbs component with support for truncating long labels
 * and displaying the full label in a tooltip on hover.
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.items - An array of breadcrumb items.
 * Each item should have a `label` (string) and an optional `path` (string).
 * Example: [{ label: 'Home', path: '/' }, { label: 'Dashboard' }]
 * @param {number} [props.maxLength=33] - The maximum length for a label before it is truncated.
 */
export default function BreadcrumbCustom({ items, maxLength = 33 }) {
  if (!items || items.length === 0) {
    return null;
  }

  /**
   * Truncates a string in the middle if it exceeds the maximum length.
   * @param {string} label The string to truncate.
   * @returns {string} The original string or the truncated string.
   */
  const truncateLabel = (label) => {
    if (label.length <= maxLength) {
      return label;
    }
    // Calculate how many characters to show from the start and end.
    const startChars = Math.ceil(maxLength / 2) - 1;
    const endChars = Math.floor(maxLength / 2) - 1;
    const start = label.substring(0, startChars);
    const end = label.substring(label.length - endChars, label.length);
    return `${start}...${end}`;
  };

  return (
    <Breadcrumbs aria-label='breadcrumb'>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const truncatedLabel = truncateLabel(item.label);
        const needsTooltip = item.label.length > maxLength;

        // Helper function to render the link or text
        const renderBreadcrumbItem = () => {
          // Render as Link if it's not the last item and has a path
          if (!isLast && item.path) {
            return (
              <Link
                color='inherit'
                to={item.path}
                style={{ textDecoration: 'none' }}
              >
                <Typography className='text-[16px] font-medium uppercase text-[#54657E]'>
                  {truncatedLabel}
                </Typography>
              </Link>
            );
          }

          return (
            <Typography className='text-[16px] font-semibold uppercase text-[#9081D5]'>
              {truncatedLabel}
            </Typography>
          );
        };

        // Conditionally wrap the item in a Tooltip
        // Using `item.label + index` as the key for better uniqueness
        return (
          <div key={item.label + index}>
            {needsTooltip ? (
              <Tooltip title={item.label}>{renderBreadcrumbItem()}</Tooltip>
            ) : (
              renderBreadcrumbItem()
            )}
          </div>
        );
      })}
    </Breadcrumbs>
  );
}
